"use client";

import type { IPlayerProps as PlayerProps } from "@lottiefiles/react-lottie-player";
import { useInView } from "framer-motion";
import dynamic from "next/dynamic";
import * as React from "react";

// Dynamically import the Player to avoid SSR issues with document access
const Player = dynamic(
  () => import("@lottiefiles/react-lottie-player").then((mod) => mod.Player),
  {
    ssr: false,
  }
);

// PlayerEvent values for use in client-side code
const PlayerEvent = {
  Load: "load",
  Error: "error",
  Ready: "ready",
  Play: "play",
  Pause: "pause",
  Stop: "stop",
  Freeze: "freeze",
  Loop: "loop",
  Complete: "complete",
  Frame: "frame",
} as const;

// Animation instance type (from lottie-web, used by lottieRef callback)
interface AnimationInstance {
  play: () => void;
  pause: () => void;
  stop: () => void;
}

interface Props extends PlayerProps {
  playWhenVisible?: boolean;
}

export function LottiePlayer(props: Props) {
  return <Player {...props} />;
}

export const LottiePlayerPlayOnce = ({
  id,
  animation,
  className,
  ...props
}: Omit<Props, "keepLastFrame" | "lottieRef" | "onEvent" | "src"> & {
  animation: string;
  id?: string;
}) => {
  const [lottieLoaded, setLottieLoaded] = React.useState(false);
  const animationRef = React.useRef<AnimationInstance | null>(null);

  const internalId = React.useId();

  React.useEffect(() => {
    if (lottieLoaded && animationRef.current) {
      animationRef.current.play();
    }
  }, [lottieLoaded]);

  return (
    <LottiePlayer
      {...props}
      lottieRef={(instance) => {
        animationRef.current = instance;
      }}
      onEvent={(event) => {
        if (event === PlayerEvent.Load) {
          setLottieLoaded(true);
        }
      }}
      id={`${id ?? internalId}-lottie`}
      className={className}
      keepLastFrame
      src={animation}
    />
  );
};

export function LottiePlayerPlayOnceOnShow({
  delay = 0,
  ...props
}: Omit<Props, "keepLastFrame" | "lottieRef" | "onEvent"> & {
  delay?: number;
}) {
  const animationRef = React.useRef<AnimationInstance | null>(null);
  const containerRef = React.useRef(null);
  const isInView = useInView(containerRef, {
    amount: "all",
    once: true,
  });

  React.useEffect(() => {
    if (!isInView || !animationRef.current) return;

    const timeoutId = window.setTimeout(() => {
      animationRef.current?.play();
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [isInView, delay]);

  return (
    <div ref={containerRef}>
      <LottiePlayer
        {...props}
        lottieRef={(instance) => {
          animationRef.current = instance;
        }}
        keepLastFrame
      />
    </div>
  );
}
